import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IStepValidation, WizardStep } from '../../WizardStep';
import { ContentCodeSelectorStepComponent } from '../content-code-selector-step/content-code-selector-step.component';
import { ContentService } from '../../../../store/services/content.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ISelect } from '../../../../store/models/select.model';
import { EContentGreeting } from '../../../../store/models/content.model';
import { MessageService } from '../../../../store/services/message.service';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { ContactsService } from '../../../../store/services/contacts.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WizardService } from '../../wizard.service';
import { HttpErrorResponse } from '@angular/common/http';
import { IUserSessionData } from '../../../../store/models/user.model';

@Component({
  selector: 'app-contact-create-message-step',
  templateUrl: './contact-create-message-step.component.html',
  styleUrls: ['./contact-create-message-step.component.scss'],
})
export class ContactCreateMessageStepComponent
  implements OnInit, WizardStep, OnDestroy {
  static STEP_NAME = 'create-test-message';

  @ViewChild('editor', { read: EditorComponent }) editor: EditorComponent;

  editorConfig: any;

  wizardData: { [p: string]: any };

  formGroup: FormGroup;

  componentReady: boolean;

  userSession: IUserSessionData;

  greetingsSelect: ISelect<any> = {
    label: 'Select greeting',
    items: [
      {
        label: EContentGreeting.HI_FIRST_NAME,
        value: EContentGreeting.HI_FIRST_NAME,
      },
      {
        label: EContentGreeting.FIRST_NAME,
        value: EContentGreeting.FIRST_NAME,
      },
      {
        label: EContentGreeting.ALOHA,
        value: EContentGreeting.ALOHA,
      },
      {
        label: EContentGreeting.NEW,
        value: 'new',
      },
    ],
  };

  showCustomGreeting: boolean;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly contentService: ContentService,
    private readonly messageService: MessageService,
    private readonly contactsService: ContactsService,
    private readonly wizardService: WizardService,
  ) {
  }

  getData(): { [key: string]: string } {
    const data = this.formGroup.value;

    if (
      [
        EContentGreeting.FIRST_NAME,
        EContentGreeting.HI_FIRST_NAME,
        EContentGreeting.ALOHA,
      ].indexOf(data.greeting) === -1 &&
      data.greeting.indexOf('{first_name}') === -1
    ) {
      data.greeting = `${data.greeting} {first_name},`;
    }

    if (data.include_signature === true) {
      data.message += data.signature;
      delete data.include_signature;
      delete data.signature;
    }

    return data;
  }

  provideData(): any {
    return this.getData();
  }

  validate(): Promise<IStepValidation> {
    return new Promise<IStepValidation>((resolve, reject) => {
      const success = this.formGroup.valid;

      if (!success) {
        this.formGroup.markAllAsTouched();

        return resolve({
          success,
        });
      }

      const data = this.getData();
      this.messageService
        .sendTestMessage(
          data.recipients,
          data.message,
          data.subject,
          data.greeting,
          data.first_name,
        )
        .subscribe(
          result => {
            if (result && result.success) {
              return resolve({
                success: true,
              });
            }

            return reject('Something happen!');
          },
          error => {
            if (
              error instanceof HttpErrorResponse &&
              error.status === 400 &&
              error.error.code === 1002
            ) {
              return resolve({
                success: false,
                errors: error.error.message,
              });
            }
            return reject(error);
          },
        );
    });
  }

  ngOnInit() {
    this.contactsService.getReplies().subscribe(response => {
      this.editorConfig = {
        toolbar:
          'quick_response | undo redo | styleselect | bold italic | alignleft aligncenter alignright alignjustify | outdent indent',
        indent: false,
        plugins: 'link lists',
        setup: editor => {
          /* Menu items are recreated when the menu is closed and opened, so we need
             a variable to store the toggle menu item state. */
          const items = response.items.map(item => {
            return {
              type: 'menuitem',
              text: item.name,
              onAction: () => {
                editor.insertContent(item.response);
              },
            };
          });
          editor.ui.registry.addMenuButton('quick_response', {
            text: 'Select Quick Response',
            fetch: callback => {
              callback(items);
            },
          });
        },
      };

      this.formGroup = new FormGroup({
        recipients: new FormControl(this.userSession.email, [Validators.required]),
        subject: new FormControl('', [Validators.required]),
        greeting: new FormControl('', [Validators.required]),
        first_name: new FormControl(this.userSession.first_name || '', [Validators.required]),
        message: new FormControl('', [Validators.required]),
        signature: new FormControl(this.userSession.signature),
        include_signature: new FormControl(true),
      });

      this.formGroup.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() =>
          this.wizardService.setNextButtonState(this.formGroup.valid),
        );

      let typedContent: string =
        this.wizardData[ContentCodeSelectorStepComponent.STEP_NAME];

      if (typeof typedContent === 'object') {
        typedContent = null;
      }

      if (typedContent) {
        this.contentService
          .findContentByCode(typedContent)
          .subscribe(result => {
            if (result.greeting) {
              this.formGroup.get('greeting').setValue(result.greeting);
            }

            if (result.subject) {
              this.formGroup.get('subject').setValue(result.subject);
            }

            this.formGroup.get('message').setValue(
              // tslint:disable-next-line:max-line-length
              `${result.content_attention || ''}${
                result.first_benefit || ''
              }${result.second_benefit || ''}${result.content_closing || ''}${result.content_details || ''}`,
            );

            this.componentReady = true;
          });
      } else {
        this.componentReady = true;
      }
    });
  }

  onGreetingsSelection($event: string) {
    if (!$event) {
      return;
    }
    if ($event === 'new') {
      this.showCustomGreeting = true;
      this.formGroup.get('greeting').setValue('');
    } else {
      this.showCustomGreeting = false;
    }
  }

  resetGreeting() {
    this.showCustomGreeting = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }
}
